import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import {
    GitBranch,
    CalendarClock,
    Factory,
    MonitorPlay,
    Package,
    Radio,
    Anchor,
    Building2,
    ArrowUpRight,
    LogOut,
    Lock,
    UserCog,
    Download,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";
import OwnerAvatar from "@/components/OwnerAvatar";
import AvatarPromptModal from "@/components/AvatarPromptModal";

const TILES = [
    {
        id: "dev-plan",
        code: "01",
        title: "Dev Plan",
        subtitle: "Roadmap · sprints",
        icon: GitBranch,
        testId: TID.hubTileDevPlan,
        path: "/dev-plan",
        accent: "#C2410C",
    },
    {
        id: "operations",
        code: "02",
        title: "Operations",
        subtitle: "14-day · 90-day",
        icon: CalendarClock,
        testId: TID.hubTileOperations,
        path: "/operations",
        accent: "#B8B8B8",
    },
    {
        id: "production",
        code: "03",
        title: "Production",
        subtitle: "Build · sea trials",
        icon: Factory,
        testId: TID.hubTileProduction,
        path: "/production",
        accent: "#C2410C",
    },
    {
        id: "logistics",
        code: "04",
        title: "Logistics",
        subtitle: "Orders · tracking",
        icon: Package,
        testId: TID.hubTileLogistics,
        path: "/logistics",
        accent: "#52525B",
    },
    {
        id: "fleet",
        code: "06",
        title: "Fleet",
        subtitle: "Vessel status",
        icon: Radio,
        testId: TID.hubTileFleet,
        path: "/fleet",
        accent: "#9CA3AF",
    },
    {
        id: "trials",
        code: "07",
        title: "Trials",
        subtitle: "Sea trials · tests",
        icon: Anchor,
        testId: TID.hubTileTrials,
        path: "/trials",
        accent: "#C2410C",
    },
    {
        id: "bd",
        code: "08",
        title: "BD Pipeline",
        subtitle: "Deals · contracts",
        icon: Building2,
        testId: TID.hubTileBd,
        path: "/bd",
        accent: "#DBDBDB",
        role: "bd", // clearance required
    },
    {
        id: "screen-saver",
        code: "05",
        title: "Screen Saver",
        subtitle: "Product showcase",
        icon: MonitorPlay,
        testId: TID.hubTileScreenSaver,
        path: "/screensaver",
        accent: "#C2410C",
    },
];

export default function Hub() {
    const nav = useNavigate();
    const { user, logout } = useAuth();
    const roles = user?.roles || [];
    const isAdmin = roles.includes("admin");
    const [profileOpen, setProfileOpen] = useState(false);

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <div className="flex items-center justify-between px-3 md:px-8 py-2 md:py-3 border-b border-virtus-line flex-shrink-0 gap-2 md:pr-24">
                <div className="flex items-center gap-2 md:gap-4 min-w-0">
                    <img
                        src="/logos/virtus-white.png"
                        alt="VIRTUS"
                        className="h-8 md:h-10 w-auto flex-shrink-0"
                    />
                    <div className="hidden md:block h-9 w-px bg-virtus-line" />
                    <div className="min-w-0">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-virtus-orange">
                            VIRTUS AI
                        </div>
                        <div className="hidden md:block font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint truncate">
                            OPERATIONS CENTER // {user?.email}
                            {roles.length > 1 && (
                                <span className="ml-2 text-virtus-dim">
                                    · CLEARANCE: {roles.join(" · ").toUpperCase()}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                    {isAdmin && (
                        <>
                            <button
                                onClick={() => nav("/admin/import")}
                                className="hidden md:flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim px-3 py-2 border border-virtus-line hover:border-virtus-orange hover:text-virtus-orange"
                                data-testid="hub-import-monday-btn"
                                title="Import from Monday.com"
                            >
                                <Download size={12} />
                                IMPORT
                            </button>
                            <button
                                onClick={() => nav("/admin/users")}
                                className="flex items-center gap-1.5 md:gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange px-2 py-1.5 md:px-3 md:py-2 border border-virtus-orange hover:bg-virtus-orange hover:text-black"
                                data-testid={TID.hubManageUsersBtn}
                                title="Manage users"
                                aria-label="Manage users"
                            >
                                <UserCog size={12} />
                                <span className="hidden md:inline">MANAGE USERS</span>
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => setProfileOpen(true)}
                        className="flex items-center gap-2 px-1.5 py-1 md:px-2 md:py-1.5 border border-virtus-line hover:border-virtus-orange transition-colors"
                        title="Set / change your profile photo"
                        data-testid="hub-profile-btn"
                    >
                        <OwnerAvatar owner={user?.email} size={22} showName={false} />
                    </button>
                    <button
                        onClick={() => {
                            logout();
                            nav("/login");
                        }}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-stuck px-2 py-2 md:px-3 md:py-2 border border-virtus-line hover:border-virtus-stuck"
                        data-testid={TID.logoutBtn}
                        aria-label="Log out"
                    >
                        <LogOut size={12} />
                        <span className="hidden md:inline">LOGOUT</span>
                    </button>
                </div>
            </div>

            {profileOpen && (
                <AvatarPromptModal forceOpen onClose={() => setProfileOpen(false)} />
            )}

            <div className="flex-1 px-3 md:px-8 py-3 md:py-4 flex flex-col md:overflow-hidden">
                <div className="mb-3 flex-shrink-0">
                    <h1 className="font-heading font-black text-2xl md:text-3xl xl:text-4xl uppercase tracking-tighter">
                        Choose Your <span className="text-virtus-orange">AO</span>.
                    </h1>
                </div>

                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 md:grid-rows-2 auto-rows-[minmax(140px,_1fr)] md:auto-rows-auto gap-px bg-virtus-line border border-virtus-line">
                    {TILES.map((t, i) => (
                        <Tile
                            key={t.id}
                            tile={t}
                            index={i}
                            hasAccess={!t.role || roles.includes(t.role)}
                            onClick={() => {
                                if (t.role && !roles.includes(t.role)) {
                                    toast.warning(
                                        `Access to ${t.title} requires '${t.role}' clearance. Contact an admin.`
                                    );
                                    return;
                                }
                                nav(t.path);
                            }}
                        />
                    ))}
                </div>

                <div className="mt-3 hidden md:grid grid-cols-4 gap-3 flex-shrink-0 md:pr-32">
                    <SystemStat label="LINK" value="ACTIVE" color="#00E676" />
                    <SystemStat label='WAKE PHRASE' value='"HEY VIRTUS"' color="#FF5500" />
                    <SystemStat
                        label="CLEARANCE"
                        value={roles.filter((r) => r !== "operator").join(" · ").toUpperCase() || "STANDARD"}
                        color="#FFEA00"
                    />
                    <SystemStat label="OPERATOR" value={user?.email || "—"} color="#2979FF" />
                </div>
            </div>
        </div>
    );
}

function Tile({ tile, index, hasAccess, onClick }) {
    const Icon = tile.icon;
    const accent = tile.accent;
    return (
        <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, duration: 0.22 }}
            onClick={onClick}
            data-testid={tile.testId}
            className="group relative text-left transition-all p-5 flex flex-col justify-between overflow-hidden min-h-0"
            style={{
                background: hasAccess
                    ? `linear-gradient(135deg, ${accent}1A 0%, ${accent}06 40%, #0A0A0A 100%)`
                    : "#0A0A0A",
                opacity: hasAccess ? 1 : 0.55,
            }}
        >
            {/* Persistent colored left bar — thickens on hover for tactile feedback. */}
            <div
                className="absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-200 group-hover:w-[6px]"
                style={{ backgroundColor: accent }}
            />

            {/* Corner wash: a soft radial in the accent to give the tile
                its own visual identity without adding noise. */}
            <div
                className="absolute top-0 right-0 w-28 h-28 opacity-25 group-hover:opacity-50 transition-opacity duration-200 pointer-events-none"
                style={{
                    background: `radial-gradient(circle at top right, ${accent}, transparent 65%)`,
                }}
            />

            {/* Header row — icon block on the left, AO code on the right. */}
            <div className="relative flex items-start justify-between">
                <div
                    className="w-14 h-14 flex items-center justify-center border transition-transform duration-200 group-hover:scale-[1.06]"
                    style={{
                        borderColor: `${accent}70`,
                        backgroundColor: `${accent}18`,
                        boxShadow: hasAccess ? `0 0 0 1px ${accent}22` : "none",
                    }}
                >
                    <Icon size={28} strokeWidth={1.75} style={{ color: accent }} />
                </div>
                <div className="flex items-center gap-2">
                    {!hasAccess && <Lock size={14} className="text-virtus-stuck" />}
                    <span
                        className="font-mono text-[11px] tracking-[0.32em] uppercase font-semibold"
                        style={{ color: accent }}
                    >
                        AO/{tile.code}
                    </span>
                </div>
            </div>

            {/* Title + subtitle. font-bold (700) with slightly positive tracking is
                classic uppercase typography — still strong, no smash. */}
            <div className="relative mt-3">
                <div
                    className="font-heading font-bold text-2xl xl:text-3xl uppercase tracking-[0.02em] leading-tight"
                    style={{ color: hasAccess ? "#F5F5F5" : "#666" }}
                >
                    {tile.title}
                </div>
                <div className="mt-2 text-xs text-virtus-dim leading-snug">
                    {hasAccess ? tile.subtitle : `RESTRICTED · Requires '${tile.role}' clearance`}
                </div>
            </div>

            {/* Footer — arrow + hint. */}
            <div className="relative flex items-center justify-between font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint mt-3">
                <span className="opacity-70 group-hover:opacity-100 transition-opacity">
                    {hasAccess ? "TAP TO ENTER" : "LOCKED"}
                </span>
                <ArrowUpRight
                    size={16}
                    className="transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1"
                    style={{ color: hasAccess ? accent : "#666" }}
                />
            </div>
        </motion.button>
    );
}

function SystemStat({ label, value, color }) {
    return (
        <div className="border border-virtus-line p-2.5 bg-virtus-surface">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                {label}
            </div>
            <div
                className="font-mono text-xs font-bold mt-0.5 truncate"
                style={{ color }}
            >
                {value}
            </div>
        </div>
    );
}
